<template>
  <div id="app">
    <keep-alive>
      <router-view v-if="$route.meta.keepAlive">
      </router-view>
    </keep-alive>
    <router-view v-if="!$route.meta.keepAlive">
    </router-view>
    <alicaptcha v-if="apiValidating && !isCloseValidatePopup"/>
  </div>
</template>

<script>
import { delCookie } from "@/utils/index.js"
import { commonConfigObj } from '@/constant'
import { mapGetters } from 'vuex';
import {
  applyNavChromeMetrics,
  isNativeChrome,
  readSafeTopPx,
  setSafeTopCssPx,
  shouldUseLegacyNav,
} from '@/utils/navChrome';

function applyRouteToken(token) {
  if (typeof token !== "string") return false;
  const value = token.trim();
  if (!value || value === "undefined" || value === "null") return false;
  return value;
}

export default {
  name: "App",
  components: {
    alicaptcha: () => import(/* webpackChunkName: "alicaptcha" */ '@/components/alicaptcha/index.vue')
  },
  computed: {
    ...mapGetters(['apiValidating','isCloseValidatePopup'])
  },
  watch: {
    $route(val) {
      // 小程序 / 跨项目跳转写入登录信息（须尽早，勿等 mounted）
      const token = applyRouteToken(val.query.token);
      if (token) {
        this.HTTP.setAuth(token);
        window.localStorage.setItem("token", token);
      }
    },
  },
  // 必须在 created：子页 charts 的 created 会立刻拉报告，晚于 mounted 会导致「没有token」
  created() {
    this.initWebToken();
  },
  mounted() {
    // 初始化
    this.JSBridge.init();
    applyNavChromeMetrics();
    this.JSBridge.registerHandler("toast", (data, responseHandler) => {
      this.$toast(data || "What up?");
      responseHandler("JS Callback");
    });
    // 注册消息监听
    this.$bus.on("sscm-relogin", (data) => {
      if (window.mobileRuntime.isApp) {
        this.JSBridge.callHandler("clearLogin");
      } else {
          if(sessionStorage.getItem("platform")) {
            delCookie(`${sessionStorage.getItem("type")}_token`);
            if(sessionStorage.getItem("type") === "1") {
                location.href = `https://open-api.shop.ele.me/authorize?response_type=code&scope=all&state=sscm&client_id=zJW53JAukO&redirect_uri=https%3A%2F%2Fwebclient.sscanmou.com%2F%23%2Felement%2Fhome%2F1`
            } else {
                location.href = `https://open-api.shop.ele.me/authorize?response_type=code&scope=all&state=sscm&client_id=MskKMDxHHy&redirect_uri=https%3A%2F%2Fwebclient.sscanmou.com%2F%23%2Felement%2Fhome%2F2`
            }
          } else {
              this.$router.push("/download");
          }
      }
    });
    // 检测是否客户端
    if (window.mobileRuntime.isApp) {

      // 尝试获得版本号
      this.JSBridge.callHandler("getVersion", null, function (code) {
        window._appCode = ~~code;

      });
      this.JSBridge.callHandler("getVersionCode", null, (code) => {
        window._appCode = ~~code;
        try {
          localStorage.setItem('sscm_app_version', String(window._appCode));
        } catch (e) { /* ignore */ }
        applyNavChromeMetrics(window._appCode);
        this.reinforceSafeTopFromBridge();
      });
      // 尝试获取并写入登录信息
      this.JSBridge.callHandler("getAuthorization", null, (token) => {

        this.HTTP.setAuth(token);
      });
      if (window.mobileRuntime.isIOS) {
        // 兼容iOS 处理token获取
        const ua = navigator.userAgent;
        const token = ua.substr(ua.indexOf("TOKEN/") + 6, 32);
        this.HTTP.setAuth(token);
      } else if (window.mobileRuntime.isAndroid && window["sscm"]) {
        // 兼容安卓 提供同步函数 处理获取延迟问题
        this.HTTP.setAuth(window.sscm.getToken());
      }
      this.reinforceSafeTopFromBridge();
    } else {
      // Web token 已在 created 处理；此处再兜底一次
      this.initWebToken();
    }
    // bridge / token 等初始化完成后挂载调试面板并打印项目标识
    this.initVConsole();
  },
  methods: {
    reinforceSafeTopFromBridge() {
      if (isNativeChrome() || shouldUseLegacyNav(window._appCode)) return;
      if (readSafeTopPx() > 0) return;
      this.JSBridge.callHandler('getDeviceInfo', null, (raw) => {
        try {
          const info = typeof raw === 'string' ? JSON.parse(raw) : raw;
          const h = Number(info && (info.safeAreaTop != null ? info.safeAreaTop : info.statusBarHeight));
          if (h > 0) {
            setSafeTopCssPx(h);
            return;
          }
        } catch (e) { /* ignore */ }
        this.JSBridge.callHandler('getStatusBarHeight', null, (px) => {
          const physical = parseFloat(String(px));
          if (!(physical > 0)) return;
          setSafeTopCssPx(physical / (window.devicePixelRatio || 1));
        });
      });
    },
    initWebToken() {
      if (window.mobileRuntime && window.mobileRuntime.isApp) return;
      let token = applyRouteToken(this.$route.query.token);
      // 处理小程序环境 token 缺失
      if (window.mobileRuntime.isMiniProgram && !token) {
        token = applyRouteToken(commonConfigObj.wx_default_token);
      }
      if (token) {
        this.HTTP.setAuth(token);
        window.localStorage.setItem("token", token);
        return;
      }
      const localToken = window.localStorage.getItem("token");
      this.HTTP.setAuth(localToken);
    },
    async initVConsole() {
      // 非生产：客户端或小程序挂载 VConsole
      if (process.env.NODE_ENV !== "production" && (window.mobileRuntime.isApp || window.mobileRuntime.isMiniProgram)) {
        const { default: VConsole } = await import(/* webpackChunkName: "vconsole" */ "vconsole");
        // eslint-disable-next-line no-new
        new VConsole({ disableLogScrolling: false, defaultPlugins: ["system", "network"], maxLogNumber: 200 });
      }
      // 生产环境也会打印；用动态取 log，避免被 drop_console 删掉
      const log = window["console"] && window["console"]["log"];
      if (typeof log === "function") {
        const hostname = window.location.hostname;
        const isLocalPage = hostname === "localhost"
          || hostname === "127.0.0.1"
          || hostname === "::1"
          || /^10\./.test(hostname)
          || /^192\.168\./.test(hostname)
          || /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);
        const pageEnv = isLocalPage ? "local" : process.env.NODE_ENV === "production" ? "production" : "test";
        const apiEnv = this.HTTP.getApiEnv();
        const apiUrl = this.HTTP.getApiUrl();
        const reportUrl = this.HTTP.getReportUrl();
        log.call(window.console, "当前项目: client-web");
        log.call(window.console, "页面运行环境: " + pageEnv);
        log.call(window.console, "API环境: " + apiEnv);
        log.call(window.console, "API地址: " + apiUrl);
        log.call(window.console, "报告嵌套地址: " + reportUrl);
        log.call(window.console, "当前页面地址: " + window.location.href);
      }
    },
  },
};
</script>
